import { queryClient } from '@/shared/api/queryClient';
import { type InfiniteData, type QueryKey } from '@tanstack/react-query';
import type { FileListItem } from 'quadratic-shared/typesAndSchemas';
import { useMemo } from 'react';

type FileListPage = { items: FileListItem[]; nextCursor: string | null };
type FileListInfiniteData = InfiniteData<FileListPage>;

/**
 * Centralized invalidation for the dashboard's file-list React Query caches
 * (`useRecentFilesQuery` and `useFolderFilesQuery`).
 *
 * Call this after any mutation that could change file list contents
 * (create, rename, delete, move, duplicate, restore, share-toggle, etc.).
 *
 * Safe to call outside React (e.g. from a route loader) — the QueryClient is
 * a module singleton shared with the `<QueryClientProvider>` in `index.tsx`.
 */
export const invalidateFileLists = (teamUuid?: string) => {
  if (teamUuid) {
    queryClient.invalidateQueries({ queryKey: ['recentFiles', teamUuid] });
    queryClient.invalidateQueries({ queryKey: ['folderFiles', teamUuid] });
    queryClient.invalidateQueries({ queryKey: ['documents', teamUuid] });
  } else {
    queryClient.invalidateQueries({ queryKey: ['recentFiles'] });
    queryClient.invalidateQueries({ queryKey: ['folderFiles'] });
    queryClient.invalidateQueries({ queryKey: ['documents'] });
  }
};

/**
 * Optimistically removes one or more files from every active file-list
 * React Query cache so rows disappear from the dashboard the moment the
 * mutation starts, rather than after the API and the subsequent refetch
 * both complete.
 *
 * Returns a revert function that restores the previous cache state. Callers
 * should invoke it from a catch block when the underlying API call fails so
 * the removed rows reappear.
 *
 * Pass `teamUuid` when known to scope the cache scan; omit to scan every team.
 */
export const removeFilesFromCacheOptimistic = (uuids: string[], teamUuid?: string): (() => void) => {
  if (uuids.length === 0) return () => {};
  const uuidSet = new Set(uuids);

  const filters: Array<{ queryKey: QueryKey }> = teamUuid
    ? [{ queryKey: ['recentFiles', teamUuid] }, { queryKey: ['folderFiles', teamUuid] }]
    : [{ queryKey: ['recentFiles'] }, { queryKey: ['folderFiles'] }];

  const snapshots: Array<[QueryKey, FileListInfiniteData | undefined]> = [];

  for (const filter of filters) {
    const matched = queryClient.getQueriesData<FileListInfiniteData>(filter);
    for (const [key, data] of matched) {
      snapshots.push([key, data]);
      if (!data) continue;
      const next: FileListInfiniteData = {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.filter((item) => !uuidSet.has(item.uuid)),
        })),
      };
      queryClient.setQueryData(key, next);
    }
  }

  return () => {
    for (const [key, prevData] of snapshots) {
      queryClient.setQueryData(key, prevData);
    }
  };
};

/**
 * React-hook wrapper around `invalidateFileLists` and
 * `removeFilesFromCacheOptimistic`. Returned values are stable across
 * renders so they're safe to drop into `useEffect`/`useCallback` deps.
 *
 * Components that aren't inside React can call the standalone functions
 * directly; both share the same singleton QueryClient.
 */
export const useFileMutationInvalidation = () => {
  return useMemo(
    () => ({
      invalidateFileLists,
      removeFilesFromCacheOptimistic,
    }),
    []
  );
};
